import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import { Ellipsis, ExternalLink } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChannelIcon } from "@/lib/channel-icons"
import { useFormatDateTime } from "@/lib/datetime"
import { ConfirmDeleteDialog } from "@/components/pieces/dialogs/ConfirmDeleteDialog"
import { PipelineSubNav } from "../PipelineSubNav"

type ArchivedPiece = {
  id: number
  name: string
  channel: string
  channel_name: string | null
  channel_type: string | null
  channel_color: string | null
  publishing_status: string
  published_url: string | null
  published_at: string | null
  archived_at: string | null
}

export default function Archive({ pieces }: { pieces: ArchivedPiece[] }) {
  const fmt = useFormatDateTime()

  return (
    <>
      <Head title="Archived pieces">
        <meta name="description" content="Archived content pieces." />
        <meta property="og:title" content="Archived pieces" />
        <meta property="og:description" content="Archived content pieces." />
      </Head>
      <AppShell wide>
        <div className="flex items-end justify-between border-b border-hairline pb-6">
          <h1>Pipeline</h1>
        </div>
        <PipelineSubNav active="archive" />

        {pieces.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">
            No archived pieces yet. Published pieces auto-archive 21 days after
            their publish date; you can also archive any piece from its “…” menu.
          </p>
        ) : (
          <ul className="mt-8 divide-y divide-hairline rounded-md border border-hairline">
            {pieces.map((p) => (
              <ArchivedPieceRow key={p.id} piece={p} fmt={fmt} />
            ))}
          </ul>
        )}
      </AppShell>
    </>
  )
}

function ArchivedPieceRow({ piece, fmt }: { piece: ArchivedPiece; fmt: ReturnType<typeof useFormatDateTime> }) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2">
      <Link href={`/pieces/${piece.id}`} className="flex min-w-0 flex-1 items-center gap-2 no-underline text-ink-body hover:text-ink-display">
        <ChannelIcon channelType={piece.channel_type ?? ""} color={piece.channel_color} className="h-3.5 w-3.5" />
        <span className="truncate">{piece.name}</span>
        {piece.archived_at && (
          <span className="text-xs text-ink-muted">· archived {fmt(piece.archived_at)}</span>
        )}
        {piece.published_at && (
          <span className="text-xs text-ink-muted">· published {fmt(piece.published_at)}</span>
        )}
        {piece.published_url && (
          <a
            href={piece.published_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </Link>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => router.post(`/pieces/${piece.id}/actions/unarchive`, {}, { preserveScroll: true })}
        >
          Unarchive
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="More actions"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
            >
              <Ellipsis className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[12rem]">
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); setConfirmDelete(true) }}
              className="text-danger-display"
            >
              Delete piece
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmDeleteDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete “${piece.name}”?`}
        description="This permanently removes the archived piece. Cannot be undone."
        confirmLabel="Delete piece"
        deleteUrl={`/pieces/${piece.id}`}
      />
    </li>
  )
}
